import { Component } from '@theme/component';
import { debounce, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Invalidate warmed geometry when hydrated menu content is inserted while
   * the menu is closed.
   *
   * @type {MutationObserver}
   */
  #menuMutationObserver = new MutationObserver(() => {
    this.#menuGeometry = new WeakMap();
    this.#headerVisibleHeight = null;
    this.#geometryWarmupQueue = [];

    // Keep the active submenu out of idle measurements until the menu closes.
    if (this.#state.activeItem) return;

    this.#scheduleGeometryWarmup();
  });

  /**
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #deactivateTimer = null;

  /**
   * Geometry is warmed outside the pointer interaction and reused while the
   * menu is opened. This preserves the existing height-based animation while
   * avoiding a forced layout on the normal hover path.
   *
   * @type {WeakMap<HTMLElement, { submenuHeight: number, headerVisibleHeight: number, isDefaultSlot: boolean }>}
   */
  #menuGeometry = new WeakMap();

  /**
   * @type {HTMLElement[]}
   */
  #geometryWarmupQueue = [];

  /**
   * @type {number | null}
   */
  #geometryWarmupId = null;

  /**
   * @type {boolean}
   */
  #geometryWarmupUsesIdleCallback = false;

  /**
   * @type {number | null}
   */
  #headerVisibleHeight = null;

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#menuMutationObserver.observe(this, { childList: true, subtree: true });
    this.#scheduleGeometryWarmup();

    // Web fonts can change menu wrapping. Rewarm after they settle without
    // putting that work on the first pointer interaction.
    document.fonts?.ready.then(() => {
      if (!this.isConnected) return;

      this.#menuGeometry = new WeakMap();
      this.#headerVisibleHeight = null;
      this.#geometryWarmupQueue = [];
      this.#scheduleGeometryWarmup();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#menuMutationObserver.disconnect();
    this.#clearDeactivateTimer();
    this.#cancelGeometryWarmup();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#menuGeometry = new WeakMap();
    this.#headerVisibleHeight = null;
    this.#geometryWarmupQueue = [];
    setHeaderMenuStyle();
    this.#scheduleGeometryWarmup();
  }, 100);

  /**
   * Warm one menu's geometry during an idle period. Processing one menu per
   * idle callback keeps the initial warmup from becoming a long task.
   */
  #warmNextMenuGeometry = () => {
    this.#geometryWarmupId = null;

    if (!this.isConnected || !this.getClientRects().length) {
      this.#geometryWarmupQueue = [];
      return;
    }

    const item = this.#geometryWarmupQueue.shift();

    if (item && !this.#menuGeometry.has(item)) {
      this.#measureAndCacheGeometry(item);
    }

    this.#scheduleGeometryWarmup();
  };

  #scheduleGeometryWarmup = () => {
    if (this.#geometryWarmupId !== null || !this.isConnected) return;

    if (!this.#geometryWarmupQueue.length) {
      const menuItems = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[ref="menuitem"]'));
      this.#geometryWarmupQueue = Array.from(menuItems).filter((item) => !this.#menuGeometry.has(item));
    }

    if (!this.#geometryWarmupQueue.length) return;

    if (typeof window.requestIdleCallback === 'function') {
      this.#geometryWarmupUsesIdleCallback = true;
      this.#geometryWarmupId = window.requestIdleCallback(this.#warmNextMenuGeometry, { timeout: 2000 });
      return;
    }

    this.#geometryWarmupUsesIdleCallback = false;
    this.#geometryWarmupId = window.setTimeout(this.#warmNextMenuGeometry, 200);
  };

  #cancelGeometryWarmup() {
    if (this.#geometryWarmupId === null) return;

    if (this.#geometryWarmupUsesIdleCallback && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(this.#geometryWarmupId);
    } else {
      window.clearTimeout(this.#geometryWarmupId);
    }

    this.#geometryWarmupId = null;
    this.#geometryWarmupQueue = [];
  }
  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#clearDeactivateTimer();

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // A user interaction takes priority over the low-priority warmup. If the
    // cache is not ready yet, the existing synchronous measurement below is
    // used as a safe first-interaction fallback.
    this.#cancelGeometryWarmup();

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    const cachedGeometry = this.#menuGeometry.get(item);
    const cachedGeometryForSlot = cachedGeometry?.isDefaultSlot === isDefaultSlot ? cachedGeometry : null;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      previouslyActiveItem.closest('.menu-list__list-item')?.removeAttribute('data-menu-item-active');
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    item.closest('.menu-list__list-item')?.setAttribute('data-menu-item-active', '');

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    this.#cleanupMutationObserver();

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Monitor DOM mutations for the entire time the submenu is open. This
      // keeps cached geometry correct when deferred content is injected after
      // the idle warmup has already completed.
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (this.#state.activeItem !== item) return;

            const updatedGeometry = this.#measureAndCacheGeometry(item, submenu, isDefaultSlot, hasSubmenu);

            if (updatedGeometry.submenuHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${updatedGeometry.submenuHeight}px`);
              this.#setFullOpenHeaderHeight(updatedGeometry.submenuHeight, updatedGeometry.headerVisibleHeight);
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
    }

    const geometry = cachedGeometryForSlot ?? this.#measureAndCacheGeometry(item, submenu, isDefaultSlot, hasSubmenu);
    const finalHeight = geometry.submenuHeight;

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, geometry.headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const item = this.#state.activeItem;
    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu =
      event.relatedTarget instanceof Node &&
      ((item instanceof HTMLElement && item.contains(event.relatedTarget)) || menu?.contains(event.relatedTarget));
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu) return;

    if (event.type === 'pointerleave') {
      this.#scheduleDeactivate(item);
      return;
    }

    this.#deactivate();
  }

  /**
   * Delay pointer-driven closing so the cursor can cross the small visual gap
   * between the top-level menu item and the absolutely positioned submenu.
   * @param {HTMLElement | null} item
   */
  #scheduleDeactivate(item) {
    this.#clearDeactivateTimer();

    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = null;
      const submenu = findSubmenu(item);
      const activeElement = document.activeElement;
      const focusIsInsideMenu =
        activeElement instanceof Node &&
        ((item instanceof HTMLElement && item.contains(activeElement)) || submenu?.contains(activeElement));

      if (item?.matches(':hover') || submenu?.matches(':hover') || focusIsInsideMenu) return;

      this.#deactivate(item);
    }, 140);
  }

  #clearDeactivateTimer() {
    if (!this.#deactivateTimer) return;

    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = null;
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.#clearDeactivateTimer();

    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.#cleanupMutationObserver();

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.closest('.menu-list__list-item')?.removeAttribute('data-menu-item-active');

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }

    this.#scheduleGeometryWarmup();
  };

  /**
   * Measure a menu outside the pointer interaction and cache the values that
   * drive the existing CSS animation.
   *
   * @param {HTMLElement} item
   * @param {HTMLElement | null} [knownSubmenu]
   * @param {boolean} [knownIsDefaultSlot]
   * @param {boolean} [knownHasSubmenu]
   * @returns {{ submenuHeight: number, headerVisibleHeight: number, isDefaultSlot: boolean }}
   */
  #measureAndCacheGeometry = (item, knownSubmenu, knownIsDefaultSlot, knownHasSubmenu) => {
    const isDefaultSlot = knownIsDefaultSlot ?? !item.closest('[slot="overflow"]');
    const foundSubmenu = knownSubmenu ?? findSubmenu(item);
    const hasSubmenu = knownHasSubmenu ?? Boolean(foundSubmenu);
    const submenu = foundSubmenu ?? (!isDefaultSlot ? this.overflowMenu : null);

    if (!submenu) {
      const emptyGeometry = { submenuHeight: 0, headerVisibleHeight: 0, isDefaultSlot };
      this.#menuGeometry.set(item, emptyGeometry);
      return emptyGeometry;
    }

    const wasActive = submenu.hasAttribute('data-active');

    // Hidden submenus use contain-intrinsic-size. Temporarily mark the
    // submenu active so this measurement uses its real content height.
    if (!wasActive) submenu.dataset.active = '';

    const geometry = this.#calculateGeometry(submenu, isDefaultSlot, hasSubmenu);

    if (!wasActive) delete submenu.dataset.active;

    const cachedGeometry = { ...geometry, isDefaultSlot };
    this.#menuGeometry.set(item, cachedGeometry);
    return cachedGeometry;
  };

  /**
   * Calculate the same dimensions used by the original activation path.
   * @param {HTMLElement} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @param {number | null} [knownSubmenuHeight]
   * @returns {{ submenuHeight: number, headerVisibleHeight: number }}
   */
  #calculateGeometry = (submenu, isDefaultSlot, hasSubmenu, knownSubmenuHeight = null) => {
    let finalHeight = (knownSubmenuHeight ?? submenu.offsetHeight) || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    const headerVisibleHeight = finalHeight > 0 ? this.#getHeaderVisibleHeight() : 0;

    return {
      submenuHeight: finalHeight,
      headerVisibleHeight,
    };
  };

  /**
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    const cachedHeaderVisibleHeight = this.#headerVisibleHeight;
    if (cachedHeaderVisibleHeight !== null) return cachedHeaderVisibleHeight;

    const headerComponent = this.headerComponent;
    if (!headerComponent) return 0;

    const isOverlapSituation = headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const topHeaderRow = headerComponent.querySelector('.header__row--top');
    const topHeaderRowHeight = topHeaderRow instanceof HTMLElement ? topHeaderRow.offsetHeight : 0;

    this.#headerVisibleHeight =
      isOverlapSituation && headerComponent.offsetHeight > 0
        ? topHeaderRowHeight
        : headerComponent.offsetHeight;

    return this.#headerVisibleHeight;
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    // Keep the active submenu rendered during an open-menu remeasure. Its height
    // is included in the overflow measurement below, and hiding it can flicker.
    const activeSubmenu = findSubmenu(this.#state.activeItem);

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      if (submenu === activeSubmenu) return;

      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      if (submenu === activeSubmenu) return;

      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number | null} [headerVisibleHeight]
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight = null) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen
      ? 0
      : submenuHeight + (headerVisibleHeight ?? this.#getHeaderVisibleHeight());

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
